import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { PageableDataSource } from "common/data/pageable-data-source.logic";
import { ProcessInterfacesService } from "management/process-interfaces/process-interfaces.service";
import { WorkspaceListStore } from "management/process-interfaces/workspace-list/workspace-list-store.logic";
import { MutexService } from "common/concurrency/mutex.service";
import { DataViewComponent, Mode } from "common/data/data-view.component";
import { debounceTime, map, takeUntil, tap } from "rxjs/operators";
import { combineLatest, Subject } from "rxjs";
import { SelectionRepository } from "common/selection/state/selection.repository";
import { getIdKeyOfTypeId } from "common/selection/helper/selection-helper";
import { InterfaceState } from "management/process-interfaces/process-interfaces.component";
import { ControlContainer, FormGroup } from "@angular/forms";
import TypedId = stages.core.TypedId;

type ProcessInterfaceWorkspace = stages.management.interfaces.ProcessInterfaceWorkspace;
type ReleasePreviewSetting = stages.process.subcomponent.interfaces.core.ReleasePreviewSetting;

interface ReleaseStatus {
	messageKey: string;
	iconClass: string;
}

@Component({
	selector: "stages-process-interfaces-workspace-list",
	templateUrl: "./process-interfaces-workspace-list.component.html",
	styleUrls: ["./process-interfaces-workspace-list.component.scss"],
	viewProviders: [
		{
			provide: ControlContainer,
			useFactory: () => inject(ControlContainer, { skipSelf: true, optional: true }),
		},
	],
})
export class ProcessInterfacesWorkspaceListComponent implements OnInit, OnDestroy {
	private destroy$ = new Subject<boolean>();

	interfaceFormGroup = new FormGroup({});

	static entityType = "workspace";
	@ViewChild("dataView", { static: false })
	dataView!: DataViewComponent<ProcessInterfaceWorkspace>;

	@Input()
	mode: Mode = Mode.VIEW;

	@Input()
	showReleaseWarning: boolean = false;

	@Input()
	menuItems?: MenuItem[];

	@Input()
	showSelectAllSwitch: boolean = true;

	@Input()
	useRouteNavigation: boolean = true;

	_forceShowOnlyChanges: boolean = false;
	_showOnlyChanges: boolean = false;
	_withWorkflowPolicy: boolean = false;

	get withWorkflowPolicy(): boolean {
		return this._withWorkflowPolicy;
	}

	@Input()
	set withWorkflowPolicy(value: boolean | undefined) {
		this._withWorkflowPolicy = value === true;
		if (this.workspaceListStore) {
			this.workspaceListStore._withWorkflowPolicy = !!value;
			this.dataSource.reloadPage();
		}
	}

	get showOnlyChanges(): boolean | undefined {
		return this._showOnlyChanges;
	}

	@Input()
	set showOnlyChanges(value: boolean | undefined) {
		this._forceShowOnlyChanges = !!value;
		this._showOnlyChanges = !!value;
		if (value !== undefined) {
			this.updateWorkspaceListStore(value);
		}
	}

	@Input()
	releasePreviewSetting: ReleasePreviewSetting = "OFF";

	@Output()
	readonly workspaceClicked = new EventEmitter<string>();

	@Output()
	readonly workspacesSelected = new EventEmitter<string[]>();

	ProcessInterfacesWorkspaceSelection = ProcessInterfacesWorkspaceListComponent;

	// until now we can  not handle multiple pageable datasources on one site
	// so we want to disable it
	pageSize: number = 10_000;
	dataSource!: PageableDataSource<ProcessInterfaceWorkspace>;
	workspaceListStore?: WorkspaceListStore;

	// TODO static identfier can cause problems when using the component more than once in the same place
	static storeIdentifier: string;

	parentContainer = inject(ControlContainer);

	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private processInterfacesService: ProcessInterfacesService,
		private mutexService: MutexService,
		private readonly selectionRepository: SelectionRepository,
	) {
		// default storeIdentifier
		ProcessInterfacesWorkspaceListComponent.storeIdentifier = "processInterfaces";
	}

	ngOnInit(): void {
		this.processInterfacesService.setTargetWorkspaceId(undefined);
		this.actualOrParentFormGroup.setErrors({ "interfaces are loading": true });
		// optional storeIdentifier can be overwritten by routing

		combineLatest([this.route.paramMap, this.route.queryParamMap, this.route.data])
			.pipe(
				// ST-36698: debounceTime avoids that this is called multiple times (per Obvservable in
				// combineLatest: https://medium.com/@rienkeisma/combinelatest-fires-twice-f0acd8c7bec4)
				debounceTime(0),
				map(async ([urlParamMap, urlQueryParamMap, routeData]) => {
					if (urlParamMap.has("page")) {
						this.processInterfacesService.setWorkspacePage(Number.parseInt(urlParamMap.get("page")!, 10));
					}

					if (urlQueryParamMap.has("pvForInterfaces")) {
						if (!this.menuItems) {
							this.setupMenuItems(urlQueryParamMap.get("pvForInterfaces")!);
						}
					} else {
						await this.router.navigate([], {
							queryParams: { pvForInterfaces: urlParamMap.get("processVersion") },
							replaceUrl: true,
						});
					}

					if (routeData.storeIdentifier) {
						ProcessInterfacesWorkspaceListComponent.storeIdentifier = routeData.storeIdentifier;
					}

					if (urlParamMap.has("interfaceState") && !this._forceShowOnlyChanges) {
						this._showOnlyChanges = urlParamMap.get("interfaceState") === "modified";
					}
					// datasource
					if (this.workspaceListStore === undefined) {
						this.setupStoreAndDataSource(this._showOnlyChanges);
					} else if (
						// ST-36698: Avoid double requests on initial loading of component
						this.storeNeedsUpdate()
					) {
						this.workspaceListStore.showOnlyChanges = this._showOnlyChanges;
						this.dataSource.reloadPage();
					}
				}),
				tap(() => {
					this.actualOrParentFormGroup.setErrors(null);
				}),
				takeUntil(this.destroy$),
			)
			.subscribe();
	}

	private storeNeedsUpdate(): boolean {
		return this.workspaceListStore !== undefined && this.workspaceListStore._showOnlyChanges !== this._showOnlyChanges;
	}

	private setupStoreAndDataSource(showOnlyChanges: boolean = false): void {
		this.workspaceListStore = new WorkspaceListStore(
			this.processInterfacesService,
			this.pageSize,
			this.withWorkflowPolicy,
			this.releasePreviewSetting ?? "OFF",
		);
		this.workspaceListStore.showOnlyChanges = showOnlyChanges;
		this.dataSource = new PageableDataSource(
			this.workspaceListStore,
			this.mutexService,
			this.router,
			this.route,
			"page",
		);
	}

	private setupMenuItems(selectedProcessVersion: string): void {
		this.menuItems = [
			{
				name: "management.process.interfaces.page.workspace.open",
				iconClass: "ico ico-navigate-to",
				disabled: () => {
					return false;
				},
				routerLink: (listEntry: ProcessInterfaceWorkspace) => [
					"//",
					"workspace",
					listEntry.id,
					selectedProcessVersion,
					"process",
				],
			},
		];
	}

	onWorkspaceClicked(processInterfaceWorkspace: ProcessInterfaceWorkspace): void {
		this.workspaceClicked.emit(processInterfaceWorkspace.id);
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- angular uses any for the commands as well
	getWorkspaceLink(processInterfaceWorkspace: ProcessInterfaceWorkspace): any[] {
		return [
			"./",
			processInterfaceWorkspace.id,
			this._forceShowOnlyChanges ? { page: 1, interfaceState: InterfaceState.MODIFIED } : { page: 1 },
		];
	}

	updateWorkspaceListStore(showOnlyChanges: boolean): void {
		if (this.storeNeedsUpdate()) {
			this.workspaceListStore!._showOnlyChanges = showOnlyChanges;
			this.dataSource.reloadPage();
		}
	}

	getStatus(processInterfaceWorkspace: ProcessInterfaceWorkspace): ReleaseStatus {
		if (
			processInterfaceWorkspace.actions.ReleaseInterfaceOfWorkspace &&
			processInterfaceWorkspace.hasApplicableChanges
		) {
			return {
				messageKey: "ok",
				iconClass: "ico-check",
			};
		} else if (processInterfaceWorkspace.actions.Release) {
			return {
				messageKey: "management.process.interfaces.page.workspaces.th3.noChanges",
				iconClass: "ico-close",
			};
		}
		return {
			messageKey: "management.process.interfaces.page.workspaces.th3.noPermission",
			iconClass: "ico-close",
		};
	}

	countSelectedItems(workspace: ProcessInterfaceWorkspace): number {
		const workspaceEntity = this.selectionRepository.getEntityCommon(
			getIdKeyOfTypeId(ProcessInterfacesWorkspaceListComponent.getIdNew(workspace)),
			ProcessInterfacesWorkspaceListComponent.storeIdentifier,
		);

		if (workspaceEntity) {
			const hasChildren = workspace.count > 0;
			// const hasChildren = workspace.selectableChildrenCount > 0;
			const selectedChildrenCountFromCommonStore =
				this.selectionRepository.getSelectedChildrenCountFromCommon(workspaceEntity) ?? 0;

			const deSelectedChildrenCount =
				this.selectionRepository.getDeSelectedChildrenCountFromCommon(workspaceEntity) ?? 0;

			const interfacesUnknown = selectedChildrenCountFromCommonStore === 0 && deSelectedChildrenCount === 0;

			let newTotal = 0;

			// TODO check if interfacesUnknown can be removed
			if (interfacesUnknown && (workspaceEntity.isSelected || workspaceEntity.indeterminate)) {
				newTotal = workspaceEntity.indeterminate
					? workspaceEntity.initiallySelectedCount ?? 0
					: workspaceEntity.selectableChildrenCount ?? 0;
			}

			return hasChildren && interfacesUnknown ? newTotal : selectedChildrenCountFromCommonStore;
		}
		return 0;
	}

	getStoreIdentifier(): string {
		return ProcessInterfacesWorkspaceListComponent.storeIdentifier;
	}

	isSelect(): boolean {
		return this.mode === Mode.SELECT;
	}

	static getId(workspace: ProcessInterfaceWorkspace): string {
		return workspace.id;
	}

	static getIdNew(workspace: ProcessInterfaceWorkspace): TypedId {
		return {
			id: workspace.id,
			typeIdent: ProcessInterfacesWorkspaceListComponent.entityType,
		};
	}

	static isDisabled(workspace: ProcessInterfaceWorkspace): boolean {
		return !workspace.hasApplicableChanges;
	}

	static isPreselected(workspace: ProcessInterfaceWorkspace): boolean {
		return (
			workspace.initiallySelectedCount === workspace.selectableChildrenCount && workspace.initiallySelectedCount > 0
		);
	}

	static isPartiallySelected(workspace: ProcessInterfaceWorkspace): boolean {
		return workspace.initiallySelectedCount < workspace.selectableChildrenCount && workspace.initiallySelectedCount > 0;
	}

	ngOnDestroy(): void {
		this.selectionRepository.setSelectAllPressedCommon(false);
		this.destroy$.next(true);
		this.destroy$.complete();
	}

	get actualOrParentFormGroup(): FormGroup {
		return this.parentContainer ? (this.parentContainer.control as FormGroup) : this.interfaceFormGroup;
	}
}
